import clientService from '../clientService';
import { DeviceAdminDetail, ContentType } from 'src/api/Api';

export interface DeviceCustodyRecord {
  custody_id: string;
  device_id: string;
  project_id: string;
  assigned_at: string;
  returned_at: string | null;
  notes?: string | null;
  device?: DeviceAdminDetail;
}

export interface CustodyCheckoutRequest {
  device_id: string;
  project_id: string;
  notes?: string;
}

export interface DeploymentDeviceAssignment {
  deployment_device_id: string;
  deployment_id: string;
  device_id: string;
  assigned_by: string;
  assigned_at: string;
  returned_at: string | null;
  notes?: string | null;
  device_name?: string;
  device_asset_tag?: string;
  device_condition?: string;
  device_status?: string;
  
  reason?: string | null;
  // -END
}

export interface DeploymentDeviceHistoryResponse {
  data: DeploymentDeviceAssignment[];
  count: number;
  deployment_id: string;
}

const mapCustodyRecord = (r: any): DeviceCustodyRecord => {
  if (!r) return r;
  return {
    custody_id: r.custody_id,
    device_id: r.device_id,
    project_id: r.project_id,
    assigned_at: r.assigned_at,
    returned_at: r.returned_at !== undefined ? r.returned_at : null,
    notes: r.notes,
    device: r.device,
  };
};

/**
 * Assigns (checks out) a physical device to a project.
 */
export const checkoutDeviceToProject = async (data: CustodyCheckoutRequest): Promise<DeviceCustodyRecord> => {
  const response = await clientService.request<any, any>({
    path: `/api/v1/device-custody/checkout`,
    method: 'POST',
    body: data,
    type: ContentType.Json,
    format: 'json',
    secure: true,
  });
  return mapCustodyRecord(response.data);
};

/**
 * Unassigns (checks in) a physical device from its project (returns it to general stock).
 */
export const checkinDeviceFromProject = async (deviceId: string): Promise<DeviceCustodyRecord> => {
  const response = await clientService.request<any, any>({
    path: `/api/v1/device-custody/checkin/${deviceId}`,
    method: 'POST',
    format: 'json',
    secure: true,
  });
  return mapCustodyRecord(response.data);
};

/**
 * Lists the device custody history/current status for a project.
 */
export const getProjectDeviceCustodyList = async (projectId: string): Promise<DeviceCustodyRecord[]> => {
  const response = await clientService.request<any, any>({
    path: `/api/v1/device-custody/project/${projectId}`,
    method: 'GET',
    format: 'json',
    secure: true,
  });
  const rawRecords = (response.data && Array.isArray(response.data.data)) 
    ? response.data.data 
    : Array.isArray(response.data) 
      ? response.data 
      : [];
  return rawRecords.map(mapCustodyRecord);
};

/**
 * Lists all projects this device has been checked out to over time.
 */
export const getDeviceCustodyHistory = async (deviceId: string): Promise<DeviceCustodyRecord[]> => {
  const response = await clientService.request<any, any>({
    path: `/api/v1/device-custody/device/${deviceId}`,
    method: 'GET',
    format: 'json',
    secure: true,
  });
  const rawRecords = (response.data && Array.isArray(response.data.data)) 
    ? response.data.data 
    : Array.isArray(response.data) 
      ? response.data 
      : [];
  return rawRecords.map(mapCustodyRecord);
};

/**
 * Assigns a device to a deployment.
 */
export const assignDeviceToDeployment = async (
  deploymentId: string,
  data: { device_id: string; assigned_by: string; notes?: string }
): Promise<DeploymentDeviceAssignment> => {
  const response = await clientService.request<DeploymentDeviceAssignment, any>({
    path: `/api/v1/deployments/${deploymentId}/assign-device`,
    method: 'POST',
    body: data,
    type: ContentType.Json,
    secure: true,
  });
  return response.data;
};

/**
 * Replaces the active device for a deployment (swap).
 */
export const replaceDeploymentDevice = async (
  deploymentId: string,
  data: { new_device_id: string; assigned_by: string; notes?: string }
): Promise<DeploymentDeviceAssignment> => {
  const response = await clientService.request<DeploymentDeviceAssignment, any>({
    path: `/api/v1/deployments/${deploymentId}/replace-device`,
    method: 'PATCH',
    body: data,
    type: ContentType.Json,
    secure: true,
  });
  return response.data;
};

/**
 * Retrieves (returns) a device from deployment.
 */
export const retrieveDeviceFromDeployment = async (
  deploymentId: string,
  data: { notes?: string }
): Promise<DeploymentDeviceAssignment> => {
  const response = await clientService.request<DeploymentDeviceAssignment, any>({
    path: `/api/v1/deployments/${deploymentId}/retrieve-device`,
    method: 'PATCH',
    body: data,
    type: ContentType.Json,
    secure: true,
  });
  return response.data;
};

/**
 * Fetches the currently active device for a deployment.
 * If 404 is returned, returns null.
 */
export const getActiveDeploymentDevice = async (
  deploymentId: string
): Promise<DeploymentDeviceAssignment | null> => {
  try {
    const response = await clientService.request<DeploymentDeviceAssignment, any>({
      path: `/api/v1/deployments/${deploymentId}/active-device`,
      method: 'GET',
      format: 'json',
      secure: true,
    });
    return response.data;
  } catch (error: any) {
    if (error && error.status === 404) {
      return null;
    }
    throw error;
  }
};

/**
 * Fetches deployment assignment history (audit trail).
 */
export const getDeploymentDeviceHistory = async (
  deploymentId: string
): Promise<DeploymentDeviceHistoryResponse> => {
  const response = await clientService.request<DeploymentDeviceHistoryResponse, any>({
    path: `/api/v1/deployments/${deploymentId}/device-history`,
    method: 'GET',
    format: 'json',
    secure: true,
  });
  return response.data;
};


/**
 * Fetches all devices eligible to be assigned to the deployment.
 */
export const getEligibleDeploymentDevices = async (
  deploymentId: string
): Promise<DeviceAdminDetail[]> => {
  const response = await clientService.request<DeviceAdminDetail[], any>({
    path: `/api/v1/deployments/${deploymentId}/devices/eligible`,
    method: 'GET',
    format: 'json',
    secure: true,
  });
  return response.data || [];
};

/**
 * Reports the active device as DAMAGED.
 */
export const reportDamagedDevice = async (
  deploymentId: string,
  data: { replacement_device_id?: string; notes?: string }
): Promise<DeploymentDeviceAssignment> => {
  const response = await clientService.request<DeploymentDeviceAssignment, any>({
    path: `/api/v1/deployments/${deploymentId}/report-damaged`,
    method: 'PATCH',
    body: data,
    type: ContentType.Json,
    format: 'json',
    secure: true,
  });
  return response.data;
};

/**
 * Reports the active device as MISSING.
 */
export const reportMissingDevice = async (
  deploymentId: string,
  data: { replacement_device_id?: string; notes?: string }
): Promise<DeploymentDeviceAssignment> => {
  const response = await clientService.request<DeploymentDeviceAssignment, any>({
    path: `/api/v1/deployments/${deploymentId}/report-missing`,
    method: 'PATCH',
    body: data,
    type: ContentType.Json,
    format: 'json',
    secure: true,
  });
  return response.data;
};

export interface DeviceTypeResponse {
  device_type_id: string;
  name: string;
  category: string;
  prefix: string;
  description?: string | null;
  is_active: boolean;
}

export interface DeviceTypeCreateRequest {
  name: string;
  category: string;
  prefix: string;
  description?: string;
  is_active?: boolean;
}

export const getDeviceTypes = async (): Promise<DeviceTypeResponse[]> => {
  const response = await clientService.request<DeviceTypeResponse[], any>({
    path: `/api/v1/admin/devices/types`,
    method: 'GET',
    format: 'json',
    secure: true,
  });
  return response.data || [];
};

export const getDeviceStatuses = async (): Promise<string[]> => {
  const response = await clientService.request<string[], any>({
    path: `/api/v1/admin/devices/statuses`,
    method: 'GET',
    format: 'json',
    secure: true,
  });
  return response.data || [];
};

export const getDeviceConditions = async (): Promise<string[]> => {
  const response = await clientService.request<string[], any>({
    path: `/api/v1/admin/devices/conditions`,
    method: 'GET',
    format: 'json',
    secure: true,
  });
  return response.data || [];
};

/**
 * Creates a new device type (Admin only).
 */
export const createDeviceType = async (
  payload: DeviceTypeCreateRequest,
): Promise<DeviceTypeResponse> => {
  const response = await clientService.request<DeviceTypeResponse, any>({
    path: `/api/v1/admin/devices/types`,
    method: 'POST',
    body: payload,
    type: ContentType.Json,
    format: 'json',
    secure: true,
  });
  return response.data;
};

